import {
	QuoteProductThreadKind,
	useGetQuoteProductThreadsQuery,
	useOnQuoteProductThreadCountsUpdatedSubscription,
	useSubscriptionRestored,
	type GetQuoteProductThreadsQuery,
} from '@codezo/graphql';
import { useCallback, useEffect, useState } from 'react';
import { useQueryParam } from '../../../hooks/useQueryParam';

export type QuoteThread = GetQuoteProductThreadsQuery['getQuoteProductThreads'][number];

/**
 * A thread's key in the URL: its supplier's id, or `studio` for the studio
 * notes. Readable, and what a comment notification's target already carries.
 */
export const threadKey = (t: Pick<QuoteThread, 'vetterEntityId'>) => t.vetterEntityId ?? 'studio';

/**
 * Every product thread on a quote, kept live, and which one is open.
 *
 * Fetched once, then kept current by `quoteProductThreadCountsUpdated`: each
 * event is one thread in the same shape — replace it by id, or add it (a
 * supplier thread that just appeared). `unreadCount` is the viewer's own, and
 * their own read arrives as an event too, on every tab they have open.
 *
 * The open panel lives in the URL — `?comments=<orderQuoteItemId>` and
 * `&thread=<vetterEntityId | studio>` — so a comment notification can land on
 * the right tab.
 */
export function useQuoteThreads(orderQuoteId: string | undefined) {
	const { data, refetch } = useGetQuoteProductThreadsQuery({
		variables: { orderQuoteId: orderQuoteId ?? '' },
		fetchPolicy: 'no-cache',
		skip: !orderQuoteId,
	});

	const [threads, setThreads] = useState<QuoteThread[]>([]);
	useEffect(() => {
		if (data) setThreads(data.getQuoteProductThreads);
	}, [data]);

	const upsert = useCallback(
		(thread: QuoteThread) =>
			setThreads((prev) =>
				prev.some((t) => t.id === thread.id)
					? prev.map((t) => (t.id === thread.id ? thread : t))
					: [...prev, thread]
			),
		[]
	);

	// Re-opened after a backend restart: the counts may have moved in the gap.
	useSubscriptionRestored(() => void refetch(), ['OnQuoteProductThreadCountsUpdated']);

	useOnQuoteProductThreadCountsUpdatedSubscription({
		variables: { orderQuoteId: orderQuoteId ?? '' },
		skip: !orderQuoteId,
		onData: ({ data: event }) => {
			const thread = event.data?.quoteProductThreadCountsUpdated;
			if (thread) upsert(thread);
		},
	});

	const [openItemId, setOpenItemId] = useQueryParam('comments', '');
	const [threadParam, setThreadParam] = useQueryParam('thread', '');

	/** A product's threads: studio notes first, then suppliers by name. */
	const threadsFor = useCallback(
		(orderQuoteItemId: string) =>
			threads
				.filter((t) => t.orderQuoteItemId === orderQuoteItemId)
				.sort(
					(a, b) =>
						Number(b.kind === QuoteProductThreadKind.Studio) -
							Number(a.kind === QuoteProductThreadKind.Studio) ||
						(a.vetterEntity?.name ?? '').localeCompare(b.vetterEntity?.name ?? '')
				),
		[threads]
	);

	/**
	 * The row's figures, recomputed from its threads on every change — and
	 * which threads the unread messages are in, so the row can say where.
	 */
	const summaryFor = (orderQuoteItemId: string) => {
		const list = threadsFor(orderQuoteItemId);
		return {
			unread: list.reduce((n, t) => n + t.unreadCount, 0),
			count: list.reduce((n, t) => n + t.commentCount, 0),
			unreadIn: list
				.filter((t) => t.unreadCount > 0)
				.map((t) => ({
					label:
						t.kind === QuoteProductThreadKind.Studio
							? 'Studio notes'
							: (t.vetterEntity?.name ?? 'Supplier'),
					unread: t.unreadCount,
				})),
		};
	};

	/**
	 * Where a product's panel starts: Studio notes, always (it sorts first). The
	 * tab badges say where anything new is. A notification link names its own
	 * thread instead.
	 */
	const startingThread = (orderQuoteItemId: string) => threadsFor(orderQuoteItemId)[0] ?? null;

	/* The tab is chosen once, when the panel opens, and pinned in the URL —
	   after that only a click moves it. */
	const open = (orderQuoteItemId: string, key?: string) => {
		const start = key ? null : startingThread(orderQuoteItemId);
		setOpenItemId(orderQuoteItemId);
		setThreadParam(key ?? (start ? threadKey(start) : ''));
	};
	const close = () => {
		setOpenItemId('');
		setThreadParam('');
	};

	const openThreads = openItemId ? threadsFor(openItemId) : [];
	const activeThread =
		openThreads.find((t) => threadKey(t) === threadParam) ?? openThreads[0] ?? null;

	// Opened from a URL with no tab (a reload, say): pin the starting one too,
	// once the threads are known.
	useEffect(() => {
		if (!openItemId || threadParam || openThreads.length === 0) return;
		const start = startingThread(openItemId);
		if (start) setThreadParam(threadKey(start));
		// eslint-disable-next-line react-hooks/exhaustive-deps -- pin once per opening
	}, [openItemId, threadParam, openThreads.length]);

	return {
		threads,
		summaryFor,
		unreadOf: (t: QuoteThread) => t.unreadCount,
		openItemId: openItemId || null,
		openThreads,
		activeThread,
		open,
		close,
		/** By the thread's tab id (its `id`). */
		setActiveThread: (id: string) => {
			const t = openThreads.find((x) => x.id === id);
			if (t) setThreadParam(threadKey(t));
		},
		/**
		 * Drops the badge the moment the thread is read. The server's own event
		 * follows and is what stands — this only saves the wait.
		 */
		markLocallyRead: (id: string) =>
			setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unreadCount: 0 } : t))),
	};
}
